import React, {
	useState,
	useContext,
	useMemo,
	createContext,
	type ReactNode,
	type Context,
	type ComponentType,
	useCallback,
} from 'react';
import noop from 'lodash/noop';
import { useRelayEnvironment } from 'react-relay';
import { commitLocalUpdate } from 'relay-runtime';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { createAri } from '@atlassian/jira-platform-ari';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import RelayDataID from '@atlassian/relay-data-id';

import { PROJECTS_ITEM_TYPE } from '../model/constants';
import type {
	ChangeFavouritePayload,
	Item,
	Items,
	FavouriteChangeContextType,
} from '../model/types';
import updateFavourite from '../services/update-favourite';

import { errorFlag } from './flags/failure';

type State = FavouriteChangeContextType;

export type Props = {
	children: ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onShowFlag?: any; // ShowFlagFn ('@atlaskit/flag')
};

export const updateItemFC = (item: Item) => (state: Items) => ({
	...state,
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	[item.type as string]: {
		...state[item.type],
		[item.id]: item,
	},
});

export const updateItem = (item: Item) => (state: State) => ({
	...state,
	items: {
		...state.items,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[item.type as string]: {
			...state.items[item.type],
			[item.id]: item,
		},
	},
});

export const defaultContextValue: FavouriteChangeContextType = {
	items: {
		boards: {},
		filters: {},
		issues: {},
		dashboards: {},
		projects: {},
		queues: {},
		plans: {},
	},
	changeFavourite: () => new Promise(noop),
	updateFavouriteState: noop,
};

export const FavouriteChangeContext: Context<FavouriteChangeContextType> =
	createContext(defaultContextValue);

// supports project types only for now
const useRelayFavouriteUpdate = () => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();

	const commitUpdate = useCallback(
		({ id, type, value }: { id: string; type: string; value: boolean }) => {
			if (type === PROJECTS_ITEM_TYPE) {
				commitLocalUpdate(environment, (store) => {
					const ari = createAri({
						resourceOwner: 'jira',
						resourceType: 'project',
						resourceId: id,
						cloudId,
					});
					const projectRecordId = RelayDataID({ id: ari }, 'JiraProject');

					if (projectRecordId) {
						const projectRecord = store.get(projectRecordId);

						const favouriteRecord = projectRecord?.getLinkedRecord('favouriteValue');
						favouriteRecord?.setValue(value, 'isFavourite');
					}
				});
			}
		},
		[cloudId, environment],
	);

	return { commitUpdate };
};

export const FavouriteChangeProvider = ({ onShowFlag, children }: Props) => {
	const { commitUpdate } = useRelayFavouriteUpdate();
	const [items, setItems] = useState<Items>({
		boards: {},
		filters: {},
		dashboards: {},
		issues: {},
		projects: {},
		queues: {},
		plans: {},
	});

	const changeFavourite = useCallback(
		(item: ChangeFavouritePayload, analyticsEvent?: UIAnalyticsEvent) => {
			const performChange = async () => {
				setItems(updateItemFC({ ...item, pending: true }));

				try {
					commitUpdate({ type: item.type, id: item.id, value: item.value });
					await updateFavourite(item);
					setItems(updateItemFC({ ...item, pending: false }));
					if (analyticsEvent) {
						fireTrackAnalytics(
							analyticsEvent,
							// removing last letter to make item.type singular tense (i.e. boards => board)
							`${item.type.slice(0, item.type.length - 1)} ${item.value ? 'starred' : 'unstarred'}`,
							`${item.id}`,
						);
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					commitUpdate({ type: item.type, id: item.id, value: !item.value });
					setItems(updateItemFC({ ...item, value: !item.value, pending: false }));

					if (onShowFlag) {
						onShowFlag(errorFlag());
					}
				}
			};

			return performChange();
		},
		[onShowFlag, commitUpdate],
	);

	const updateFavouriteState = useCallback((item: ChangeFavouritePayload) => {
		setItems(updateItemFC({ ...item, pending: false }));
	}, []);

	const providerValue = useMemo(
		() => ({
			items,
			changeFavourite,
			updateFavouriteState,
		}),
		[items, changeFavourite, updateFavouriteState],
	);

	return (
		<FavouriteChangeContext.Provider value={providerValue}>
			{children}
		</FavouriteChangeContext.Provider>
	);
};

export const FavouriteChangeConsumer = FavouriteChangeContext.Consumer;

export const withFavouriteChangeConsumer = <
	P,
	W extends P & {
		favouriteChangeContext: FavouriteChangeContextType;
	},
>(
	WrappedComponent: ComponentType<W>,
): ComponentType<P> => {
	const WithFavouriteChangeConsumer = (props: P) => (
		<FavouriteChangeConsumer>
			{(favouriteChangeContext) => (
				// @ts-expect-error - TS2322 - Type 'P & { favouriteChangeContext: FavouriteChangeContextType; }' is not assignable to type 'IntrinsicAttributes & W & { children?: ReactNode; }'.
				<WrappedComponent {...props} favouriteChangeContext={favouriteChangeContext} />
			)}
		</FavouriteChangeConsumer>
	);

	const wrappedDisplayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
	WithFavouriteChangeConsumer.displayName = `WithFavouriteChangeConsumer(${wrappedDisplayName})`;
	return WithFavouriteChangeConsumer;
};

export const useChangeFavourite = () => useContext(FavouriteChangeContext);
